import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Datagrid,
  DateField,
  List,
  TextField,
  TopToolbar,
  FunctionField,
  ReferenceField,
  useGetList,
  useUpdate,
  useRecordContext,
  useRefresh,
  useListContext,
  useListController,
  NumberField
} from 'react-admin';
import {
  Switch,
  Box,
  Button,
  Menu,
  MenuItem,
  TextField as MuiTextField,
  ListItemIcon,
  Chip,
  Typography,
  Select,
  InputLabel,
  FormControl,
  useTheme,
  useMediaQuery
} from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import BackupTableIcon from '@mui/icons-material/BackupTable';
import AddIcon from '@mui/icons-material/Add';
import { DeleteGameRecords } from './dialogs/DeleteGameRecords';
import { ActionsMenu, ActionsMenuItem } from '../../components/ActionsMenu';
import UpdateGameDialog from './dialogs/UpdateGameDialog';
import { Loader } from '../Loader';
import { gameRecordsExports } from '../../Utilities/allExports';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import CustomPagination from '../../components/CustomPagination';
import { NewGameDialog } from './dialogs/NewGameDialog';

const CustomFilter = () => {
  const [gameVariant, setGameVariant] = useState('All');
  const [gameName, setGameName] = useState('');
  const [buildingId, setBuildingId] = useState('All');

  const { data: buildingData } = useGetList(
    'VegasBuilding',
    {
      pagination: { page: 1, perPage: 10 },
      sort: { field: 'createdAt', order: 'ASC' }
    },
    {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false
    }
  );

  const { setFilters } = useListContext();

  const handleApply = () => {
    const filters = {};
    if (gameVariant !== 'All') filters.type = gameVariant;
    if (buildingId !== 'All') filters.building = buildingId;
    if (gameName) filters.name = gameName;
    setFilters(filters, {});
  };

  const handleClear = () => {
    setGameVariant('All');
    setBuildingId('All');
    setGameName('');
    setFilters({}, {});
  };

  return (
    <Box display={{ xs: 'block', width: '100%', sm: 'flex' }} alignItems="center" gap={2}>
      <Box display="flex" width="max-content">
        <MuiTextField
          size="small"
          variant="outlined"
          value={gameName}
          onChange={(e) => setGameName(e.target.value)}
          placeholder="Game Name"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleApply();
            }
          }}
        />

        <FormControl>
          <InputLabel>Game</InputLabel>
          <Select
            size="small"
            value={gameVariant}
            label="Game"
            onChange={(e) => setGameVariant(e.target.value)}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="All">All</MenuItem>
            <MenuItem value="VE">VE</MenuItem>
          </Select>
        </FormControl>

        <FormControl>
          <InputLabel>Building</InputLabel>
          <Select
            size="small"
            value={buildingId}
            label="Building"
            onChange={(e) => setBuildingId(e.target.value)}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="All">All</MenuItem>
            {buildingData?.map((ele) => (
              <MenuItem key={ele.id} value={ele.id}>
                {ele.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box display="flex" width="max-content">
        <Button
          variant="contained"
          sx={{
            marginTop: '10px',
            marginBottom: '10px',
            marginRight: '10px',
            background: '#FFC107',
            color: '#0A0A0A',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: '#FFA000'
            }
          }}
          onClick={handleApply}
        >
          Apply
        </Button>
        <Button
          variant="outlined"
          sx={{
            marginTop: '10px',
            marginBottom: '10px',
            marginRight: '10px',
            outline: '1px solid #FFC107',
            color: 'white',
            textTransform: 'none',
            border: 'none'
          }}
          onClick={handleClear}
        >
          Clear
        </Button>
      </Box>
    </Box>
  );
};

const BooleanToggle = ({ source }) => {
  const refresh = useRefresh();
  const record = useRecordContext();
  const [update, { isLoading }] = useUpdate();

  if (!record) return null;

  const handleToggle = () => {
    update(
      'gameCatalogueSwitch',
      {
        id: record.id,
        data: { [source]: !record[source] }
      },
      {
        onSuccess: () => refresh()
      }
    );
  };

  return (
    <React.Fragment>
      <Switch checked={record[source]} onChange={handleToggle} disabled={isLoading} />
      {isLoading && <Loader />}
    </React.Fragment>
  );
};

export const GameRecordList = () => {
  const { isLoading, total, page, perPage, setPage, setPerPage } = useListController();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [isExporting, setIsExporting] = useState(false);

  // Create Game Dialog state
  const [createGameOpen, setCreateGameOpen] = useState(false);
  const handleCreateGameOpen = () => setCreateGameOpen(true);
  const handleCreateGameClose = () => setCreateGameOpen(false);

  const navigate = useNavigate();

  const currentUserData = localStorage.getItem(`Parse/${process.env.REACT_APP_APP_ID}/currentUser`);

  if (!currentUserData) {
    navigate('/login');
  }

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [filters] = useState({});

  const handleMenuOpen = (event) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  // export the data in xls formate
  const handleExportXLS = async () => {
    try {
      setIsExporting(true);

      const exportData = await gameRecordsExports(filters);

      if (!exportData || !exportData?.data || exportData?.data?.length === 0) {
        console.warn('No data to export.');
        return;
      }

      const selectedFields = exportData.data.map((item) => ({
        'Game Name': item.name,
        Active: item.isActive ? 'Yes' : 'No',
        'Game Variant': item.type || '-',
        'Object ID': item.id || '-',
        Slug: item.slug || '-',
        'Game URL': item.gameURL || '-',
        Category: item.category || '-',
        'Vegas Building': item.buildingName || '-',
        'Game Sequence': item.gameSequence || '-',
        'Recording Time': new Intl.DateTimeFormat('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        }).format(new Date(item.createdAt))
      }));

      const worksheet = XLSX.utils.json_to_sheet(selectedFields);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Game Records');
      const xlsData = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      saveAs(new Blob([xlsData], { type: 'application/octet-stream' }), 'GameRecords.xlsx');
    } catch (error) {
      console.error('Error fetching user engagement:', error);
    } finally {
      setIsExporting(false);
      handleMenuClose();
    }
  };

  const listActions = (
    <TopToolbar>
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        sx={{
          background: '#FFC107',
          color: '#0A0A0A',
          textTransform: 'none',
          '&:hover': {
            backgroundColor: '#FFA000'
          }
        }}
        onClick={handleCreateGameOpen}
      >
        Create New Game
      </Button>
      <Button
        variant="contained"
        startIcon={<GetAppIcon />}
        sx={{
          marginLeft: '10px',
          background: '#FFC107',
          color: '#0A0A0A',
          textTransform: 'none',
          '&:hover': {
            backgroundColor: '#FFA000'
          }
        }}
        onClick={handleMenuOpen}
      >
        Export
      </Button>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button'
        }}
        sx={{ marginTop: 1 }}
      >
        {/* <MenuItem onClick={handleExportPDF}>
          <ListItemIcon>
            <PictureAsPdfIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            PDF file
          </Typography>
        </MenuItem> */}

        <MenuItem onClick={handleExportXLS} disabled={isExporting}>
          <ListItemIcon>
            <BackupTableIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {isExporting ? 'Exporting...' : 'Excel file'}
          </Typography>
        </MenuItem>
      </Menu>
    </TopToolbar>
  );

  if (isLoading) {
    return <Loader />;
  }

  return (
    <React.Fragment>
      <Box
        sx={{
          mt: 4,
          backgroundColor: '#242424',
          borderRadius: 2,
          boxShadow: 'none !important',
          padding: isSmallScreen ? { xs: 0, sm: 0 } : { xs: 1.5, sm: 2 }
        }}
      >
        <List
          title="Game Records"
          filters={<CustomFilter />}
          actions={listActions}
          pagination={false}
          sort={{ field: 'createdAt', order: 'DESC' }}
          sx={{
            '& .RaList-actions': {
              flexWrap: 'nowrap'
            },
            '& .RaFilterFormInput-spacer': { display: 'none' }
          }}
        >
          <Box
            style={{
              width: '100%',
              overflowX: 'auto'
            }}
          >
            <Box
              style={{
                width: '100%',
                position: isSmallScreen ? 'absolute' : 'relative'
              }}
            >
              <Datagrid
                bulkActionButtons={false}
                sx={{
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  width: '100%',
                  maxHeight: '100%',
                  '& .RaDatagrid-table': {
                    width: '100%'
                  }
                }}
              >
                <ActionsMenu label="Actions">
                  <ActionsMenuItem>
                    Edit
                    <UpdateGameDialog />
                  </ActionsMenuItem>
                  <ActionsMenuItem>
                    Delete
                    <DeleteGameRecords />
                  </ActionsMenuItem>
                </ActionsMenu>
                <BooleanToggle source="isActive" label="Active" />
                <TextField source="name" label="Game Name" />
                <FunctionField
                  label="Game Variant"
                  source="type"
                  render={(record) => (
                    <Chip
                      label={record.type}
                      color={record.type === 'VE' ? 'primary' : 'default'}
                      size="small"
                      variant="outlined"
                    />
                  )}
                />
                <FunctionField label="Object ID" render={(record) => record.id || '-'} />
                <TextField source="category" label="Category" />
                <ReferenceField
                  source="building.id"
                  reference="VegasBuilding"
                  label="Vegas Building"
                  emptyText="Missing Building"
                  link={false}
                  sortBy="building.name"
                >
                  <TextField source="name" />
                </ReferenceField>
                <NumberField source="gameSequence" label="Game Sequence" textAlign="left" />
                <TextField source="live_version" label="Live Version" />
                <DateField source="createdAt" label="Recording Time" showTime />
              </Datagrid>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  width: '100% !important',
                  margin: '16px 0px'
                }}
              >
                <CustomPagination
                  page={page}
                  perPage={perPage}
                  total={total}
                  setPage={setPage}
                  setPerPage={setPerPage}
                />
              </Box>
            </Box>
          </Box>
        </List>
      </Box>

      {/* Create Game Dialog rendered directly */}
      <NewGameDialog open={createGameOpen} handleClose={handleCreateGameClose} />
    </React.Fragment>
  );
};
